import { t } from '@/i18n/i18n'
import { ErrorService } from '@/shared/service/errorService'
import { getErrorMessage } from '@/shared/types/http.type'
import {
  type AnyCategory,
  type Category,
  type EditableCategory,
  type Genre,
  type GenreUsage,
  isTemporaryCategory,
  type Tag,
} from '@/shared/types/musicGroup/musicGroup.type'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

import { type Lang } from '../../../../types/TranslatableText.type'
import { GenresService } from '../genres.service'

class GuestGenresState {
  genres: Record<string, Genre> = {}
  availableCategories: AnyCategory[] = []
  availableTags: Genre[] = []
  unusedGenreCount = 0
  lastDeletedCount = 0
  lastDeletedCategoriesCount = 0
  deletingGenreIds: string[] = []
  deletingGenreUsages: GenreUsage | null = null
  errorMessage: string | null = null
  selectedGenreRows: Genre[] = []
}

type LoadAvailableCategoriesPayload = {
  context: 'ALL' | 'GUEST' | 'EVENT'
  guestTypeId?: string
  programId?: string
}

type GuestGenresStore = GuestGenresState & {
  // Mutations → actions
  reset: () => void
  setGenres: (genres: Genre[]) => void
  addAvailableCategory: (category: AnyCategory) => void
  updateCategory: (category: Category) => void
  removeTemporaryCategories: () => void
  setGenre: (genre: Genre) => void
  removeGenre: (genreId: string) => void
  setAvailableCategories: (categories: AnyCategory[]) => void
  setAvailableTags: (tags: Genre[]) => void
  setUnusedGenreCount: (count: number) => void
  setLastDeletedCount: (count: number) => void
  setDeletingGenreIds: (ids: string[]) => void
  setDeletingGenreUsages: (usages: GenreUsage | null) => void
  setErrorMessage: (message: string | null) => void
  setSelectedGenreRows: (genres: Genre[]) => void
  // Actions
  addGenres: (genre: Genre | Tag) => Promise<void>
  getGenres: () => Promise<void>
  deleteGenre: (genre: Genre) => Promise<void>
  updateGenre: (genre: Genre) => Promise<void>
  loadAvailableCategories: (
    payload: LoadAvailableCategoriesPayload,
  ) => Promise<void>
  loadAvailableTags: () => Promise<void>
  countUnusedGenres: () => Promise<void>
  deleteUnusedGenres: () => Promise<void>
  prepareDeletion: (genreId: string) => Promise<void>
  cancelDeletion: () => void
  prepareMultipleDeletion: () => Promise<void>
  deleteMultipleGenres: () => Promise<void>
  updateGenresCategory: (payload: {
    ids: string[]
    category: Category | EditableCategory
  }) => Promise<void>
}

export const useGuestGenresStore = create<GuestGenresStore>((set, get) => ({
  ...new GuestGenresState(),

  // --- Mutations → actions ---

  reset: () => {
    set({
      genres: {},
      deletingGenreIds: [],
      deletingGenreUsages: null,
      selectedGenreRows: [],
    })
  },

  setGenres: (genres) => {
    set({
      genres: genres.reduce((acc: Record<string, Genre>, genre: Genre) => {
        acc[genre._id] = genre
        return acc
      }, {}),
    })
  },

  addAvailableCategory: (category) => {
    set({ availableCategories: [...get().availableCategories, category] })
  },

  /**
   * Updates an existing category or inserts a new one in the correct order.
   * Also propagates changes to all genres utilizing this category.
   */
  updateCategory: (category) => {
    if (!category._id) return

    const matchesName = (c: AnyCategory) =>
      (['fr', 'en'] as Lang[]).every(
        (lang) =>
          c.names[lang].trim().toLowerCase() ===
          category.names[lang].trim().toLowerCase(),
      )

    const currentCats = get().availableCategories
    const index = currentCats.findIndex(
      (c) => c._id === category._id || matchesName(c),
    )

    let newCategories: AnyCategory[]
    if (index === -1) {
      const insertIndex = currentCats.findIndex(
        (c) => (c.index ?? 0) > category.index,
      )
      newCategories = [...currentCats]
      if (insertIndex === -1) {
        newCategories.push(category)
      } else {
        newCategories.splice(insertIndex, 0, category)
      }
    } else {
      newCategories = [...currentCats]
      newCategories.splice(index, 1, category)
    }

    // Propagate updates to genres using this category
    const newGenres = { ...get().genres }
    let updatedGenreCount = 0
    Object.keys(newGenres).forEach((id) => {
      const genre = newGenres[id]
      if (genre.category?._id === category._id) {
        newGenres[id] = { ...genre, category }
        updatedGenreCount += 1
      }
    })

    set({
      availableCategories: newCategories,
      ...(updatedGenreCount > 0 ? { genres: newGenres } : {}),
    })
  },

  removeTemporaryCategories: () => {
    set({
      availableCategories: get().availableCategories.filter(
        (c) => !isTemporaryCategory(c),
      ),
    })
  },

  setGenre: (genre) => {
    set({ genres: { ...get().genres, [genre._id]: genre } })
  },

  removeGenre: (genreId) => {
    const { [genreId]: _removed, ...rest } = get().genres
    void _removed
    set({
      genres: rest,
      selectedGenreRows: get().selectedGenreRows.filter(
        (g) => g._id !== genreId,
      ),
    })
  },

  setAvailableCategories: (categories) => {
    set({ availableCategories: cloneDeep(categories) })
  },

  setAvailableTags: (tags) => {
    set({ availableTags: cloneDeep(tags) })
  },

  setUnusedGenreCount: (count) => {
    set({ unusedGenreCount: count })
  },

  setLastDeletedCount: (count) => {
    set({ lastDeletedCount: count })
  },

  setDeletingGenreIds: (ids) => {
    set({ deletingGenreIds: ids })
  },

  setDeletingGenreUsages: (usages) => {
    set({ deletingGenreUsages: usages })
  },

  setErrorMessage: (message) => {
    set({ errorMessage: message })
  },

  setSelectedGenreRows: (genres) => {
    set({ selectedGenreRows: genres })
  },

  // --- Actions ---

  async addGenres(genre) {
    try {
      await GenresService.addGenre(genre)
      await get().getGenres()
      await get().loadAvailableCategories({ context: 'ALL' })
      await get().countUnusedGenres()
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async getGenres() {
    try {
      const genres = await GenresService.getAll()
      get().setGenres(genres)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async deleteGenre(genre) {
    try {
      await GenresService.deleteGenre(genre._id)
      get().removeGenre(genre._id)
      await get().countUnusedGenres()
      await get().loadAvailableCategories({ context: 'ALL' })
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async updateGenre(genre) {
    // Reset error before attempt
    get().setErrorMessage(null)
    try {
      const updatedGenre = await GenresService.updateGenre(genre)
      get().setGenre(updatedGenre)

      // Refresh globally to reflect potential backend re-indexing/shifting
      await get().getGenres()
      await get().loadAvailableCategories({ context: 'ALL' })
    } catch (e) {
      const message = getErrorMessage(e, t('GENERAL.SNACKBAR_ERROR'))
      get().setErrorMessage(message as string | null)
    }
  },

  async loadAvailableCategories(payload) {
    const { context, guestTypeId, programId } = payload

    try {
      const categories = await GenresService.getAvailableCategories(
        context,
        guestTypeId,
        programId,
      )
      get().setAvailableCategories(categories)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async loadAvailableTags() {
    try {
      const tags = await GenresService.getAvailableTags()
      get().setAvailableTags(tags)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async countUnusedGenres() {
    try {
      const count = await GenresService.countUnused()
      get().setUnusedGenreCount(count)
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async deleteUnusedGenres() {
    try {
      const { deletedTagsCount, deletedCategoriesCount } =
        await GenresService.deleteUnused()
      await get().getGenres()
      await get().countUnusedGenres()
      await get().loadAvailableCategories({ context: 'ALL' })
      set({
        lastDeletedCount: deletedTagsCount,
        lastDeletedCategoriesCount: deletedCategoriesCount,
      })
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async prepareDeletion(genreId) {
    get().setDeletingGenreIds([genreId])
    try {
      const usages = await GenresService.getGenreUsages(genreId)
      get().setDeletingGenreUsages(usages)
    } catch (e) {
      ErrorService.handleError(e)
      get().setDeletingGenreUsages(null)
    }
  },

  cancelDeletion() {
    get().setDeletingGenreIds([])
    get().setDeletingGenreUsages(null)
  },

  async prepareMultipleDeletion() {
    const genreIds = get().selectedGenreRows.map((g) => g._id)
    get().setDeletingGenreIds(genreIds)
    try {
      const usages = await GenresService.getUsagesBatch(genreIds)
      get().setDeletingGenreUsages(usages)
    } catch (e) {
      ErrorService.handleError(e)
      get().setDeletingGenreUsages(null)
    }
  },

  async deleteMultipleGenres() {
    const genreIds = get().selectedGenreRows.map((g) => g._id)
    try {
      await GenresService.deleteGenresBatch(genreIds)
      await get().getGenres()
      await get().countUnusedGenres()
      await get().loadAvailableCategories({ context: 'ALL' })
      get().setLastDeletedCount(genreIds.length)
      get().setSelectedGenreRows([])
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  async updateGenresCategory({ ids, category }) {
    try {
      await GenresService.updateGenresCategory(ids, category)
      await get().getGenres()
      await get().loadAvailableCategories({ context: 'ALL' })
      get().setSelectedGenreRows([])
    } catch (e) {
      ErrorService.handleError(e)
      throw e
    }
  },
}))
